import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';

// Data for the Flurbereinigung (land consolidation) layer.
// P2/P3 shape: lean map RPCs return simplified geometries and minimal
// properties (flurbereinigung_map / flurbereinigung_overlaps, the latter
// reading the materialized overlap view); full per-procedure detail is
// fetched on click by the sidebar. Results are cached per scope for the
// session — the dataset is static.

const cache = new Map(); // scopeKey -> { proceduresFC, overlaps }

export function useFlurbereinigung(active, mode, focusBereich) {
  const [data, setData] = useState(null); // { proceduresFC, overlaps }
  const reqId = useRef(0);

  const region = mode.kind === 'region' ? mode.name : null;
  const scopeKey = active && region ? `${region}|${focusBereich ?? ''}` : null;

  useEffect(() => {
    if (!scopeKey) {
      reqId.current++;
      setData(null);
      return;
    }
    if (cache.has(scopeKey)) {
      setData(cache.get(scopeKey));
      return;
    }
    const myReq = ++reqId.current;
    const args = { p_anbaugebiet: region, p_bereich: focusBereich || null };
    Promise.all([
      supabase.rpc('flurbereinigung_map', args),
      supabase.rpc('flurbereinigung_overlaps', args),
    ]).then(([p, o]) => {
      if (myReq !== reqId.current) return;
      if (p.error) console.warn('flurbereinigung_map:', p.error.message);
      if (o.error) console.warn('flurbereinigung_overlaps:', o.error.message);
      const result = {
        proceduresFC: p.error ? null : p.data,
        overlaps: o.error ? [] : (o.data ?? []),
      };
      cache.set(scopeKey, result);
      setData(result);
    });
  }, [scopeKey, region, focusBereich]);

  const overlapsFC = useMemo(() => {
    const rows = data?.overlaps;
    if (!rows?.length) return null;
    return {
      type: 'FeatureCollection',
      features: rows
        .filter(r => r.geometry)
        .map(r => ({
          type: 'Feature',
          geometry: r.geometry,
          properties: { flurb_oid: r.flurb_oid, vineyard_id: r.vineyard_id, relevant: r.relevant },
        })),
    };
  }, [data]);

  return {
    proceduresFC: data?.proceduresFC ?? null,
    overlaps: data?.overlaps ?? null,
    overlapsFC,
  };
}
